const LOG_PREFIX = "[pdfCanvas perf]";

interface IStrokeSession {
    pageIndex: number,
    tool: string,
    startedAt: number,
    pointerMoves: number,
    forceRenderCalls: number,
    eraserCursorUpdates: number,
    redrawAllCount: number,
    redrawAllTotalMs: number,
    redrawAllMaxMs: number,
    redrawPageTotalCount: number,
    redrawPageMaxMs: number,
    loadImageMaxMs: number,
}

interface IRedrawPageResult {
    pageIndex: number,
    totalMs: number,
    loadImageMs: number,
    strokeCount: number,
    activePointCount: number,
    canvasW: number,
    canvasH: number,
    hadInitialImage: boolean,
    skipped: boolean,
    skipReason?: string,
}

let strokeSession: IStrokeSession | null = null;
let redrawVersion = 0;

function createStrokeSession(pageIndex: number, tool: string): IStrokeSession {
    return {
        pageIndex,
        tool,
        startedAt: performance.now(),
        pointerMoves: 0,
        forceRenderCalls: 0,
        eraserCursorUpdates: 0,
        redrawAllCount: 0,
        redrawAllTotalMs: 0,
        redrawAllMaxMs: 0,
        redrawPageTotalCount: 0,
        redrawPageMaxMs: 0,
        loadImageMaxMs: 0,
    };
}

export function pdfCanvasPerfStartStroke(pageIndex: number, tool: string): void {
    strokeSession = createStrokeSession(pageIndex, tool);
    console.log(LOG_PREFIX, "stroke:start", { pageIndex, tool });
}

export function pdfCanvasPerfPointerMove(): void {
    if (strokeSession) {
        strokeSession.pointerMoves += 1;
    }
}

export function pdfCanvasPerfEraserCursorUpdate(): void {
    if (strokeSession) {
        strokeSession.eraserCursorUpdates += 1;
    }
}

export function pdfCanvasPerfForceRender(source: string): void {
    if (strokeSession) {
        strokeSession.forceRenderCalls += 1;
        return;
    }

    console.log(LOG_PREFIX, "forceRender", { source, version: redrawVersion + 1 });
}

export function pdfCanvasPerfFinishStroke(pointCount: number, committed: boolean): void {
    if (!strokeSession) {
        return;
    }

    const durationMs = Math.round(performance.now() - strokeSession.startedAt);
    const {
        redrawAllCount,
        redrawAllTotalMs,
        redrawAllMaxMs,
        redrawPageTotalCount,
        redrawPageMaxMs,
        loadImageMaxMs,
        ...sessionBase
    } = strokeSession;

    console.log(LOG_PREFIX, "stroke:end", {
        ...sessionBase,
        pointCount,
        committed,
        durationMs,
        avgMsPerMove: strokeSession.pointerMoves > 0
            ? Math.round(durationMs / strokeSession.pointerMoves * 10) / 10
            : 0,
        redraw: {
            redrawAllCount,
            redrawAllAvgMs: redrawAllCount > 0 ? Math.round(redrawAllTotalMs / redrawAllCount) : 0,
            redrawAllMaxMs,
            redrawPageTotalCount,
            redrawPageAvgMs: redrawPageTotalCount > 0
                ? Math.round(redrawAllTotalMs / redrawPageTotalCount)
                : 0,
            redrawPageMaxMs,
            loadImageMaxMs,
        },
    });
    strokeSession = null;
}

export function pdfCanvasPerfRedrawAllStart(version: number, pageCount: number): number {
    redrawVersion = version;
    if (!strokeSession) {
        console.log(LOG_PREFIX, "redrawAll:start", { version, pageCount });
    }
    return performance.now();
}

export function pdfCanvasPerfRedrawAllEnd(t0: number, pageResults: IRedrawPageResult[]): void {
    const totalMs = Math.round(performance.now() - t0);
    const drawnPages = pageResults.filter(r => !r.skipped);

    if (strokeSession) {
        strokeSession.redrawAllCount += 1;
        strokeSession.redrawAllTotalMs += totalMs;
        strokeSession.redrawAllMaxMs = Math.max(strokeSession.redrawAllMaxMs, totalMs);
        strokeSession.redrawPageTotalCount += drawnPages.length;
        for (const page of drawnPages) {
            strokeSession.redrawPageMaxMs = Math.max(strokeSession.redrawPageMaxMs, page.totalMs);
            strokeSession.loadImageMaxMs = Math.max(strokeSession.loadImageMaxMs, page.loadImageMs);
        }
        return;
    }

    console.log(LOG_PREFIX, "redrawAll:end", {
        version: redrawVersion,
        totalMs,
        pageCount: pageResults.length,
        drawnPageCount: drawnPages.length,
        maxPageMs: drawnPages.length > 0 ? Math.max(...drawnPages.map(r => r.totalMs)) : 0,
        sumPageMs: drawnPages.reduce((sum, r) => sum + r.totalMs, 0),
        pages: pageResults,
    });
}

export function pdfCanvasPerfRedrawPageStart(pageIndex: number): number {
    return performance.now();
}

export function pdfCanvasPerfRedrawPageEnd(
    pageIndex: number,
    t0: number,
    details: Omit<IRedrawPageResult, "pageIndex" | "totalMs">,
): IRedrawPageResult {
    const result: IRedrawPageResult = {
        pageIndex,
        totalMs: Math.round(performance.now() - t0),
        ...details,
    };

    if (!result.skipped && !strokeSession) {
        console.log(LOG_PREFIX, "redrawCanvas", result);
    }

    return result;
}
